import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { FileText, Calendar, Clock } from 'lucide-react';
import { Author } from '@/types/blog';

interface ReportHeaderProps {
  title: string;
  excerpt: string;
  date: string;
  readTime: number;
  author: Author;
  categories: string[];
}

const ReportHeader = ({ 
  title, 
  excerpt, 
  date, 
  readTime, 
  author, 
  categories 
}: ReportHeaderProps) => {
  const formattedDate = new Date(date + 'T12:00:00').toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <header className="bg-gradient-to-b from-fordje-navy to-fordje-navy-light text-white py-16 md:py-24">
      <div className="container-custom">
        <div className="max-w-4xl mx-auto">
          {/* Report badge and categories */}
          <div className="flex flex-wrap items-center gap-2 mb-6">
            <Badge className="bg-fordje-accent text-white border-none px-3 py-1">
              <FileText className="h-3 w-3 mr-1" />
              Industry Report
            </Badge>
            {categories.map((category) => (
              <Badge 
                key={category} 
                variant="outline" 
                className="border-white/30 text-white/80 bg-white/5"
              >
                {category}
              </Badge>
            ))}
          </div>
          
          {/* Title */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
            {title}
          </h1>
          
          {/* Excerpt */}
          <p className="text-lg md:text-xl text-white/80 mb-8 leading-relaxed max-w-3xl">
            {excerpt}
          </p>
          
          {/* Meta info */}
          <div className="flex flex-wrap items-center gap-6 pt-6 border-t border-white/20">
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10 border-2 border-white/30">
                <AvatarImage src={author.avatar} alt={author.name} />
                <AvatarFallback className="bg-fordje-blue text-white">
                  {author.name.split(' ').map(n => n[0]).join('')}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="font-medium text-sm">{author.name}</p>
                <p className="text-xs text-white/60">{author.title}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4 text-sm text-white/70">
              <span className="flex items-center gap-1.5">
                <Calendar className="h-4 w-4" />
                {formattedDate}
              </span>
              <span className="flex items-center gap-1.5">
                <Clock className="h-4 w-4" />
                {readTime} min read
              </span>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default ReportHeader;
